import { motion, useReducedMotion } from 'framer-motion'
import { isRedSuit, rankLabel, suitSymbol } from '../api/client'
import { EASE_OUT, SPRING_SETTLE } from '../../../shared/motion.ts'
import cardBack from '../../../shared/assets/round6-hilo-card-back-v1.png'
import cardFace from '../../../shared/assets/round9-hilo-vellum-card-face-v1.png'

type Props = {
  rank?: number
  suit?: string
  faceDown?: boolean
  label?: string
  flipKey?: string | number
  side?: 'left' | 'right'
}

/**
 * Two-face playing card. Idle face-down uses a static back (no 3D) so Telegram
 * WebView never flashes the white front. Reveal remounts via flipKey and flips
 * back → front. Display only — rank/suit always come from the server.
 */
export function CardDisplay({
  rank,
  suit,
  faceDown = false,
  label,
  flipKey,
  side = 'left',
}: Props) {
  const red = suit ? isRedSuit(suit) : false
  const reduce = useReducedMotion()
  const showFront = !faceDown && rank != null && !!suit
  const rankText = showFront ? rankLabel(rank!) : ''
  const symbol = showFront ? suitSymbol(suit!) : ''

  // Idle / waiting: static card back only (avoids white face flash).
  if (!showFront) {
    return (
      <div className="card-slot">
        {label && <span className="card-label">{label}</span>}
        <div className="playing-card card-back-static" aria-hidden>
          <img className="card-back-image" src={cardBack} alt="" />
        </div>
      </div>
    )
  }

  return (
    <div className="card-slot">
      {label && <span className="card-label">{label}</span>}
      <motion.div
        key={String(flipKey ?? `${rank}-${suit}`)}
        className="card-3d"
        initial={
          reduce
            ? false
            : {
                opacity: 0,
                x: side === 'left' ? -28 : 28,
                y: -34,
                rotateY: 178,
                rotateZ: side === 'left' ? -4 : 4,
                scale: 0.94,
              }
        }
        animate={{ opacity: 1, x: 0, y: 0, rotateY: 0, rotateZ: 0, scale: 1 }}
        transition={
          reduce
            ? { duration: 0 }
            : {
                opacity: { duration: 0.18, ease: EASE_OUT },
                rotateY: { duration: 0.48, ease: EASE_OUT },
                default: SPRING_SETTLE,
              }
        }
        style={{ transformStyle: 'preserve-3d' }}
      >
        <div
          className={`playing-card face front ${red ? 'red' : 'black'}`}
          data-suit={suit}
          role="img"
          aria-label={`${rankText}${symbol}`}
        >
          <img className="card-face-image" src={cardFace} alt="" aria-hidden />
          <div className="corner top" aria-hidden>
            <span className="card-rank">{rankText}</span>
            <span className="card-suit">{symbol}</span>
          </div>
          <div className="center-pip" aria-hidden>
            <span className="card-suit card-suit-hero">{symbol}</span>
          </div>
          <div className="corner bottom" aria-hidden>
            <span className="card-rank">{rankText}</span>
            <span className="card-suit">{symbol}</span>
          </div>
        </div>
        <div className="playing-card face back-face" aria-hidden>
          <img className="card-back-image" src={cardBack} alt="" />
        </div>
      </motion.div>
    </div>
  )
}
